import { FC, useRef, useState } from 'react'

import { type Dayjs } from 'dayjs'
import styled from 'styled-components'

import CalendarIcon from 'src/Assets/Svg/common/calendar.svg?react'
import CrossIcon from 'src/Assets/Svg/common/fullCross.svg?react'
import RightIcon from 'src/Assets/Svg/pages-icons/RightArrow.svg?react'
import { PrimaryDottedButton } from 'src/Components/Buttons/PrimaryDottedButton'
import { PrimaryDatePicker } from 'src/Components/PrimaryDatePicker'
import { formatDate } from 'src/Constants/formatDate'

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const Field = styled.div`
  display: flex;
  min-height: 56px;
  align-items: center;
  gap: 12px;
  padding: 8px 8px 8px 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  border-radius: 16px;
  background: ${({ theme }) => theme.color.white};
`

const Trigger = styled.button`
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  gap: 12px;
  padding: 0;
  border: none;
  background: none;
  font-family: 'Segoe UI';
  text-align: left;
  cursor: pointer;
`

const TriggerIcon = styled.span`
  display: flex;
  flex-shrink: 0;
  color: ${({ theme }) => theme.color.darkGrey};
`

const Placeholder = styled.span`
  font-size: 14px;
  line-height: 150%;
  color: ${({ theme }) => theme.color.slateGrey};
`

const Value = styled.span`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
`

const ValueLabel = styled.span`
  font-size: 12px;
  line-height: 150%;
  color: ${({ theme }) => theme.color.slateGrey};
`

const ValueDate = styled.span`
  font-size: 14px;
  line-height: 150%;
  color: ${({ theme }) => theme.color.darkGrey};
`

const ClearButton = styled.button`
  display: flex;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: none;
  color: ${({ theme }) => theme.color.darkGrey};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.color.hoverWhite};
  }
`

const PickerAnchor = styled.div`
  position: relative;
  height: 0;
`

const SHADE_Z_INDEX = 1011

const Shade = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${SHADE_Z_INDEX};
  background: ${({ theme }) => theme.unchangingColor.black};
  opacity: 0.5;
`

const PrevIcon = styled(RightIcon)`
  transform: rotate(180deg);
`

const SaveButton = styled(PrimaryDottedButton)`
  height: 48px;
`

const CALENDAR_DAY_SIZE = '40px'
const CALENDAR_WIDTH = '280px'

const StyledDatePicker = styled(PrimaryDatePicker)`
  z-index: ${SHADE_Z_INDEX + 1} !important;
  top: 8px !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  padding: 0;

  &.date-picker-dropdown .date-picker-panel-container {
    padding: 15px;
  }

  &.date-picker-dropdown .date-picker-panel {
    border: none;
  }

  &.date-picker-dropdown .date-picker-date-panel {
    width: auto;
  }

  &.date-picker-dropdown .date-picker-header {
    height: 21px;
    margin-bottom: 16px;
    padding: 0;
  }

  &.date-picker-dropdown .date-picker-header > button {
    display: flex;
    align-items: center;
    padding: 0;
    line-height: 1;
  }

  &.date-picker-dropdown .date-picker-header-view {
    line-height: 150%;
  }

  &.date-picker-dropdown .date-picker-header-view > button {
    margin-inline-start: 0;
  }

  &.date-picker-dropdown .date-picker-header-view > button + button {
    margin-inline-start: 4px;
  }

  &.date-picker-dropdown .date-picker-month-panel,
  &.date-picker-dropdown .date-picker-year-panel,
  &.date-picker-dropdown .date-picker-decade-panel {
    width: ${CALENDAR_WIDTH};
  }

  &.date-picker-dropdown .date-picker-body,
  &.date-picker-dropdown .date-picker-date-panel .date-picker-body {
    padding: 0;
  }

  &.date-picker-dropdown .date-picker-date-panel .date-picker-content {
    width: auto;
    border-spacing: 0;
  }

  &.date-picker-dropdown .date-picker-date-panel .date-picker-content thead {
    display: none;
  }

  &.date-picker-dropdown .date-picker-date-panel .date-picker-content td {
    padding: 0;
  }

  &.date-picker-dropdown .date-picker-cell::before {
    display: none;
  }

  &.date-picker-dropdown
    .date-picker-date-panel
    .date-picker-header-super-prev-btn,
  &.date-picker-dropdown
    .date-picker-date-panel
    .date-picker-header-super-next-btn {
    display: none;
  }

  &.date-picker-dropdown .date-picker-month-panel .date-picker-cell-inner,
  &.date-picker-dropdown .date-picker-year-panel .date-picker-cell-inner,
  &.date-picker-dropdown .date-picker-decade-panel .date-picker-cell-inner {
    width: auto;
    height: ${CALENDAR_DAY_SIZE};
    padding: 0 8px;
    border-radius: 8px;
    line-height: ${CALENDAR_DAY_SIZE};
    white-space: nowrap;
  }

  &.date-picker-dropdown .date-picker-cell:not(.date-picker-cell-in-view) {
    visibility: hidden;
  }

  &.date-picker-dropdown
    .date-picker-date-panel
    .date-picker-content
    tr:has(> td:first-child:not(.date-picker-cell-in-view)):has(
      > td:last-child:not(.date-picker-cell-in-view)
    ) {
    display: none;
  }

  &.date-picker-dropdown
    .date-picker-date-panel
    .date-picker-cell
    .date-picker-cell-inner {
    width: ${CALENDAR_DAY_SIZE};
    min-width: ${CALENDAR_DAY_SIZE};
    height: ${CALENDAR_DAY_SIZE};
    border-radius: 8px;
    line-height: ${CALENDAR_DAY_SIZE};
  }

  &.date-picker-dropdown .date-picker-cell-in-view.date-picker-cell-disabled {
    color: ${({ theme }) => theme.color.mediumGrey};
  }

  &.date-picker-dropdown
    .date-picker-date-panel
    .date-picker-cell-in-view.date-picker-cell-today
    .date-picker-cell-inner::before {
    inset: 8px;
    border-radius: 4px;
  }

  &.date-picker-dropdown .date-picker-footer {
    margin-top: 16px;
    border-top: none;
    line-height: normal;
  }

  &.date-picker-dropdown .date-picker-footer-extra {
    display: flex;
    justify-content: center;
    padding: 0;
    border-bottom: none;
    line-height: normal;
  }

  &.date-picker-dropdown .date-picker-ranges {
    display: none;
  }
`

export const RELEASE_DATE_FORMAT = formatDate.formatDate1

export interface ReleaseDateFieldProps {
  label: string
  value: Dayjs | null
  disabledDate: (date: Dayjs) => boolean
  onChange: (date: Dayjs | null) => void
}

export const ReleaseDateField: FC<ReleaseDateFieldProps> = ({
  label,
  value,
  disabledDate,
  onChange,
}) => {
  const [open, setOpen] = useState(false)
  const [pendingDate, setPendingDate] = useState<Dayjs | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const selectedDate = pendingDate ?? value

  const openCalendar = (): void => {
    setPendingDate(null)
    setOpen(true)
  }

  const saveDate = (): void => {
    if (selectedDate) {
      onChange(selectedDate)
    }
    setOpen(false)
  }

  return (
    <Container>
      <Field>
        <Trigger type='button' onClick={openCalendar}>
          {value ? (
            <Value>
              <ValueLabel>{label}</ValueLabel>
              <ValueDate>{value.format(RELEASE_DATE_FORMAT)}</ValueDate>
            </Value>
          ) : (
            <>
              <TriggerIcon>
                <CalendarIcon />
              </TriggerIcon>
              <Placeholder>{label}</Placeholder>
            </>
          )}
        </Trigger>
        {value && (
          <ClearButton
            type='button'
            aria-label={`Clear ${label.toLowerCase()}`}
            onClick={() => onChange(null)}
          >
            <CrossIcon />
          </ClearButton>
        )}
      </Field>
      {open && <Shade />}
      <PickerAnchor ref={anchorRef}>
        <StyledDatePicker
          open={open}
          getPopupContainer={() => anchorRef.current ?? document.body}
          onOpenChange={setOpen}
          value={value}
          needConfirm
          onCalendarChange={(date) => setPendingDate(date as Dayjs | null)}
          disabledDate={disabledDate}
          showNow={false}
          superPrevIcon={<PrevIcon />}
          superNextIcon={<RightIcon />}
          renderExtraFooter={() => (
            <SaveButton
              type='button'
              disabled={!selectedDate}
              onClick={saveDate}
            >
              Save
            </SaveButton>
          )}
          style={{ visibility: 'hidden', width: 0, height: 0, padding: 0 }}
        />
      </PickerAnchor>
    </Container>
  )
}
